import React, { useEffect, useRef, useState } from "react";
import { Pagination, DatePicker } from "antd";
import dayjs from "dayjs";
import { format } from "date-fns";
import { Calendar, ChevronLeft, RefreshCw, X, User, Phone, Video, MapPin, Clock, Edit, Eye, MessageSquare, Share2, Stethoscope, Hash, CalendarCheck, Zap, Settings, ClipboardList } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DashboardLayout from "../layout/index";
import { APP_ROUTES } from "../../constants/approutes";

const { RangePicker } = DatePicker;

const joinUrl = (base, path) => {
  if (!base) return path;
  const cleanBase = base.endsWith("/") ? base.slice(0, -1) : base;
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  return `${cleanBase}/${cleanPath}`;
};

// Tries the direct storage URL first (HEAD check), then falls back to the
// utility service's signed-URL endpoint if the direct URL isn't reachable.
const resolveDocumentUrl = async (rawPath, buildDirectUrl) => {
  if (!rawPath) return null;
  if (rawPath.startsWith("http://") || rawPath.startsWith("https://")) {
    return rawPath;
  }

  const directUrl = buildDirectUrl(rawPath);

  try {
    const res = await fetch(directUrl, { method: "HEAD" });
    if (res.ok) return directUrl;
  } catch (err) {
    // Direct fetch failed (missing file, CORS, network) - fall back below.
  }

  try {
    const UTILITY_URL = process.env.REACT_APP_UTILITY_SERVICE_URL || "";
    const url = joinUrl(
      UTILITY_URL,
      `api/gcp/document-url?fileName=${encodeURIComponent(rawPath)}`
    );
    const res = await fetch(url);
    const data = await res.json().catch(() => null);
    if (res.ok && data?.SIGNED_URL) return data.SIGNED_URL;
  } catch (err) {
    // Utility service also failed.
  }

  return null;
};

const ConsultationList = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_NEW_DOCTOR_URL;
  const GOOGLE_API_URL = process.env.REACT_APP_API_GOOGLE_URL || "";
  const SHARECALL_BASE_URL = (
    process.env.REACT_APP_SHARECALL_BASE_URL ||
    process.env.REACT_APP_NEW_DOCTOR_URL ||
    ""
  )
    .replace(/\/doctor-services\/?$/, "")
    .replace(/\/$/, "");
  const DOCTOR_API_BASE_URL = (process.env.REACT_APP_NEW_DOCTOR_URL || "")
    .replace(/\/$/, "");
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [consultationFilter, setConsultationFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [appointmentIdFilter, setAppointmentIdFilter] = useState("");
  const [doctorNameFilter, setDoctorNameFilter] = useState("");
  const today = new Date();
  const todayStr = format(today, "yyyy-MM-dd");
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);
  const [appliedFromDate, setAppliedFromDate] = useState(todayStr);
  const [appliedToDate, setAppliedToDate] = useState(todayStr);
  const [isPrescriptionModalOpen, setIsPrescriptionModalOpen] = useState(false);
  const [selectedPrescriptionUrl, setSelectedPrescriptionUrl] = useState("");
  const [selectedPatientName, setSelectedPatientName] = useState("Patient");
  const [isPrescriptionLoading, setIsPrescriptionLoading] = useState(false);
  const [prescriptionError, setPrescriptionError] = useState("");
  const prescriptionRequestIdRef = useRef(0);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareRecipient, setShareRecipient] = useState("");
  const [shareError, setShareError] = useState("");
  const [shareSending, setShareSending] = useState(false);
  const [selectedSharePayload, setSelectedSharePayload] = useState(null);
  const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
  const [selectedRemarkAppointmentId, setSelectedRemarkAppointmentId] = useState("");
  const [remarkText, setRemarkText] = useState("");
  const [remarkError, setRemarkError] = useState("");
  const [remarkSaving, setRemarkSaving] = useState(false);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const FILTERS_STORAGE_KEY = "consultationListFilters_v1";
  const FILTERS_RESTORE_ONCE_KEY = "consultationListRestoreOnce_v1";

  const tableScrollRef = useRef(null);
  const tableScrollbarRef = useRef(null);
  const tableScrollbarSpacerRef = useRef(null);
  const [showTableScrollbar, setShowTableScrollbar] = useState(false);

  useEffect(() => {
    const scroller = tableScrollRef.current;
    const bar = tableScrollbarRef.current;
    const spacer = tableScrollbarSpacerRef.current;
    if (!scroller || !bar || !spacer) return;

    let syncingFromScroller = false;
    let syncingFromBar = false;

    const syncBarFromScroller = () => {
      if (!bar || syncingFromBar) return;
      syncingFromScroller = true;
      bar.scrollLeft = scroller.scrollLeft;
      syncingFromScroller = false;
    };

    const syncScrollerFromBar = () => {
      if (!scroller || syncingFromScroller) return;
      syncingFromBar = true;
      scroller.scrollLeft = bar.scrollLeft;
      syncingFromBar = false;
    };

    const updateMetrics = () => {
      const scrollWidth = scroller.scrollWidth || 0;
      const clientWidth = scroller.clientWidth || 0;
      spacer.style.width = `${scrollWidth}px`;
      setShowTableScrollbar(scrollWidth > clientWidth + 1);
      syncBarFromScroller();
    };

    scroller.addEventListener("scroll", syncBarFromScroller, { passive: true });
    bar.addEventListener("scroll", syncScrollerFromBar, { passive: true });

    const ro = new ResizeObserver(updateMetrics);
    ro.observe(scroller);
    if (scroller.firstElementChild) ro.observe(scroller.firstElementChild);
    updateMetrics();

    return () => {
      scroller.removeEventListener("scroll", syncBarFromScroller);
      bar.removeEventListener("scroll", syncScrollerFromBar);
      ro.disconnect();
    };
  }, []);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("fromDate", appliedFromDate);
      params.set("toDate", appliedToDate);
      params.set("paymentStatus", "SUCCESS");
      params.set("page", String(currentPage));
      params.set("size", String(pageSize));

      if (statusFilter !== "All") {
        params.set("status", statusFilter);
      }
      if (consultationFilter === "Video") {
        params.set("consultationsType", "V");
      } else if (consultationFilter === "In Person") {
        params.set("consultationsType", "I");
      }
      if (appointmentIdFilter.trim()) {
        params.set("appointmentId", appointmentIdFilter.trim());
      }
      if (doctorNameFilter.trim()) {
        params.set("doctorName", doctorNameFilter.trim());
      }

      const res = await fetch(`${API_URL}/appointments?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      const rows = Array.isArray(data?.data) ? data.data : [];
      setConsultations(rows);
      setTotalElements(
        Number.isFinite(data?.totalElements) ? data.totalElements : rows.length
      );
    } catch (error) {
      console.error("Failed to fetch consultation list:", error);
      setConsultations([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!filtersHydrated) return;
    fetchConsultations();
  }, [
    API_URL,
    filtersHydrated,
    currentPage,
    pageSize,
    appliedFromDate,
    appliedToDate,
    consultationFilter,
    statusFilter,
    appointmentIdFilter,
    doctorNameFilter,
  ]);

  // Restore filters only when coming back from Edit Prescription.
  useEffect(() => {
    try {
      const shouldRestore = sessionStorage.getItem(FILTERS_RESTORE_ONCE_KEY) === "1";
      const raw = shouldRestore ? sessionStorage.getItem(FILTERS_STORAGE_KEY) : null;
      if (raw && shouldRestore) {
        const parsed = JSON.parse(raw);
        const parseYMD = (value) => {
          if (!value) return null;
          const d = new Date(`${value}T00:00:00`);
          return Number.isNaN(d.getTime()) ? null : d;
        };

        const from = parseYMD(parsed?.appliedFromDate);
        const to = parseYMD(parsed?.appliedToDate);
        if (from && to) {
          const fromStr = format(from, "yyyy-MM-dd");
          const toStr = format(to, "yyyy-MM-dd");
          setFromDate(fromStr);
          setToDate(toStr);
          setAppliedFromDate(fromStr);
          setAppliedToDate(toStr);
        }

        if (
          ["All", "Video", "In Person"].includes(parsed?.consultationFilter)
        ) {
          setConsultationFilter(parsed.consultationFilter);
        }

        if (
          ["Upcoming", "Completed", "Cancelled", "All"].includes(parsed?.statusFilter)
        ) {
          setStatusFilter(
            parsed.statusFilter === "Cancelled" ? "Cancelled" : parsed.statusFilter
          );
        }
        if (typeof parsed?.appointmentIdFilter === "string") {
          setAppointmentIdFilter(parsed.appointmentIdFilter);
        }
        if (typeof parsed?.doctorNameFilter === "string") {
          setDoctorNameFilter(parsed.doctorNameFilter);
        }

        const nextPage = Number(parsed?.currentPage);
        const nextSize = Number(parsed?.pageSize);
        if (Number.isFinite(nextPage) && nextPage > 0) setCurrentPage(nextPage);
        if (Number.isFinite(nextSize) && nextSize > 0) setPageSize(nextSize);
      }
    } catch (_) {
      // ignore
    }

    // Clear one-time restore flag so it doesn't persist across unrelated navigation.
    try {
      sessionStorage.removeItem(FILTERS_RESTORE_ONCE_KEY);
    } catch (_) { }

    setFiltersHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveFiltersForReturn = () => {
    const payload = {
      appliedFromDate,
      appliedToDate,
      consultationFilter,
      statusFilter,
      appointmentIdFilter,
      doctorNameFilter,
      currentPage,
      pageSize,
    };
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(payload));
      sessionStorage.setItem(FILTERS_RESTORE_ONCE_KEY, "1");
    } catch (_) {
      // ignore
    }
  };

  const getStatusLabel = (status) => {
    const code = Number(status);
    if (code === 1 || code === 8) return "Upcoming";
    if (code === 5) return "Cancelled";
    if (code === 2) return "Completed";
    if (code === 7) return "Pending";
    if (code === 9) return "Expired";
    if (code === 16) return "Hold";
    if (code === 20) return "Processing";
    if (code === 13) return "Cancelled";

    if (code === 3) return "Cancell by patient from d2c";
    if (code === 11) return "Cancell by che center ";
    return status || "-";
  };

  const getStatusStyle = (status) => {
    const label = getStatusLabel(status);
    switch (label) {
      case "Cancelled":
        case "Cancelled by patient from d2c":
          case "Cancelled by staff ":
        return "bg-red-50 text-red-700 border-red-100";
        case "Upcoming":
          return "bg-sky-50 text-sky-700 border-sky-100";
      case "Completed":
        return "bg-emerald-50 text-emerald-700 border-emerald-100";
      case "Pending":
      case "Processing":
        return "bg-amber-50 text-amber-700 border-amber-100";
      case "Cancelled":
      case "Expired":
        return "bg-rose-50 text-rose-700 border-rose-100";
      default:
        return "bg-slate-50 text-slate-700 border-slate-100";
    }
  };

  const buildPrescriptionUrl = (prescriptionUrl) => {
    if (!prescriptionUrl) return "";

    if (
      prescriptionUrl.startsWith("http://") ||
      prescriptionUrl.startsWith("https://")
    ) {
      return prescriptionUrl;
    }

    const base = GOOGLE_API_URL.endsWith("/")
      ? GOOGLE_API_URL.slice(0, -1)
      : GOOGLE_API_URL;
    const cleanPath = prescriptionUrl.startsWith("/")
      ? prescriptionUrl.slice(1)
      : prescriptionUrl;

    if (cleanPath.includes("curebay-prescription-docs")) {
      return `${base}/${cleanPath}`;
    }

    return `${base}/curebay-prescription-docs/${cleanPath}`;
  };

  const handleOpenPrescriptionModal = async (item) => {
    const rawPath = item?.prescriptionUrl;
    if (!rawPath) return;

    const requestId = ++prescriptionRequestIdRef.current;
    const fullName = `${item?.firstName || ""} ${item?.lastName || ""}`.trim();

    setSelectedPatientName(fullName || "Patient");
    setSelectedPrescriptionUrl("");
    setPrescriptionError("");
    setIsPrescriptionLoading(true);
    setIsPrescriptionModalOpen(true);

    const finalUrl = await resolveDocumentUrl(rawPath, buildPrescriptionUrl);

    if (requestId !== prescriptionRequestIdRef.current) return; // a newer request superseded this one

    if (finalUrl) {
      setSelectedPrescriptionUrl(finalUrl);
    } else {
      setPrescriptionError("Prescription document could not be found");
    }
    setIsPrescriptionLoading(false);
  };

  const handleClosePrescriptionModal = () => {
    prescriptionRequestIdRef.current += 1; // invalidate any in-flight resolution
    setIsPrescriptionModalOpen(false);
    setSelectedPrescriptionUrl("");
    setSelectedPatientName("Patient");
    setIsPrescriptionLoading(false);
    setPrescriptionError("");
  };

  const buildShareCallUrl = (item) => {
    const appointmentId = item?.id || "";
    const consultationType = item?.consultationsType || "V";
    return `${SHARECALL_BASE_URL}/sharecall/${appointmentId}/${consultationType}`;
  };

  const handleOpenShareModal = (item) => {
    console.log(item, "itemgvhbj");
    const fullName = `${item?.firstName || ""} ${item?.lastName || ""}`.trim();
    setSelectedSharePayload({
      // url: buildShareCallUrl(item),
      // patientId: item?.patientId || item?.patientUniqueId || "",
      // userId: item?.userId || "",
      // patientName: fullName || "Patient",
      mobile: item?.mobile || item?.patientMobileNo || "",
      doctorName: item?.doctorFirstName + " " + item?.doctorLastName || "",
      patientName: item?.firstName + " " + item?.lastName || "",
      appointmentId: item?.id || "",
    });



    setShareRecipient(item?.mobile || item?.patientMobileNo || "");
    setShareError("");
    setIsShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
    setShareRecipient("");
    setShareError("");
    setSelectedSharePayload(null);
    setShareSending(false);
  };

  const handleSendShareCall = async () => {
    if (!selectedSharePayload) return;

    const payload = {
      ...selectedSharePayload,
      mobile:
        shareRecipient?.trim() || selectedSharePayload.mobile,
    };


    if (!payload.mobile) {
      setShareError("Please enter mobile number");
      return;
    }

    if (!/^\d{10}$/.test(payload.mobile)) {
      setShareError("Mobile number must be exactly 10 digits");
      return;
    }

    try {
      setShareSending(true);
      setShareError("");
      const response = await fetch(
        `${DOCTOR_API_BASE_URL}/consultation/send-link`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      console.log(response, "responseyghb");
      if (!response.ok) {
        throw new Error(`Share call failed with status ${response.status}`);
      }

      if (response.status === 200) {
        toast.success("Share call link sent successfully.");
        handleCloseShareModal();
      }
    } catch (error) {
      setShareError("Failed to send share call link. Please try again.");
    } finally {
      setShareSending(false);
    }
  };

  const handleOpenEditPrescriptionPage = (item) => {
    if (!item?.id) return;
    const fullName = `${item?.firstName || ""} ${item?.lastName || ""}`.trim();
    saveFiltersForReturn();
    navigate(`${APP_ROUTES.EDIT_PRESCRIPTION}?appointmentId=${item.id}`, {
      state: {
        appointmentId: item.id,
        patientName: fullName || "Patient",
        data: item,
      },
    });
  };

  const handleOpenRemarkModal = (item) => {
    if (!item?.id) return;
    setSelectedRemarkAppointmentId(String(item.id));
    setRemarkText(item?.remarks || "");
    setRemarkError("");
    setIsRemarkModalOpen(true);
  };

  const handleCloseRemarkModal = () => {
    setIsRemarkModalOpen(false);
    setSelectedRemarkAppointmentId("");
    setRemarkText("");
    setRemarkError("");
    setRemarkSaving(false);
  };

  const handleSubmitRemark = async () => {
    const trimmedRemark = String(remarkText || "").trim();
    if (!selectedRemarkAppointmentId) {
      setRemarkError("Appointment not found.");
      return;
    }
    if (!trimmedRemark) {
      setRemarkError("Please enter a remark.");
      return;
    }

    try {
      setRemarkSaving(true);
      setRemarkError("");
      const response = await fetch(
        `${API_URL}/appointments/${selectedRemarkAppointmentId}/remarks`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ remarks: trimmedRemark }),
        }
      );
      if (response.status === 200) {
        setRemarkText("");
        toast.success("Remark updated successfully.");
        await fetchConsultations();
        handleCloseRemarkModal();
        return;
      }

      toast.error(`Failed to update remark (${response.status}).`);
      throw new Error(`Remark update failed with status ${response.status}`);
    } catch (error) {
      setRemarkError("Failed to update remark. Please try again.");
      if (!String(error?.message || "").includes("status")) {
        toast.error("Failed to update remark. Please try again.");
      }
    } finally {
      setRemarkSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mt-8 px-6 max-w-[1800px] mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-emerald-50 p-2 rounded-xl border border-emerald-100 flex items-center justify-center">
              <ClipboardList size={26} className="text-emerald-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#334155] tracking-tight">
                Consultation List
              </h1>
            </div>
          </div>
          <button
            type="button"
            onClick={fetchConsultations}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm border border-slate-200 transition-all hover:bg-slate-50 hover:shadow-md active:scale-95"
          >
            <RefreshCw
              size={18}
              className={`${loading ? "animate-spin" : ""} text-teal-600`}
            />
            Refresh Data
            
          </button>
        </div>

        {/* Filters Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-10 transition-all hover:shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            {/* Date Filter */}
            <div className="flex flex-col gap-2 lg:col-span-2 xl:col-span-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                <Calendar size={12} className="text-teal-600" />
                Date Range
              </label>
              <RangePicker
                value={[fromDate ? dayjs(fromDate) : null, toDate ? dayjs(toDate) : null]}
                format="DD-MM-YYYY"
                allowClear={false}
                onChange={(dates) => {
                  const nextFrom = dates && dates[0] ? dates[0].format("YYYY-MM-DD") : "";
                  const nextTo = dates && dates[1] ? dates[1].format("YYYY-MM-DD") : "";
                  setFromDate(nextFrom);
                  setToDate(nextTo);
                  setAppliedFromDate(nextFrom);
                  setAppliedToDate(nextTo);
                  setCurrentPage(1);
                }}
                size="large"
                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 text-sm text-slate-700 hover:bg-white focus-within:bg-white focus-within:border-teal-500/50"
              />
            </div>

            {/* Type Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                <Video size={12} className="text-teal-600" />
                Consultation Type
              </label>
              <div className="relative">
                <select
                  value={consultationFilter}
                  onChange={(e) => {
                    setConsultationFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full h-11 appearance-none rounded-xl border border-slate-200 bg-slate-50/50 pl-4 pr-10 text-sm  text-slate-700 focus:bg-white focus:border-teal-500/50 focus:outline-none transition-all"
                >
                  <option value="All">All Types</option>
                  <option value="Video">Video Call</option>
                  <option value="In Person">In Person</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                  <ChevronLeft size={16} className="-rotate-90" />
                </div>
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-teal-500 shadow-sm shadow-teal-200" />
                Current Status
              </label>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full h-11 appearance-none rounded-xl border border-slate-200 bg-slate-50/50 pl-4 pr-10 text-sm  text-slate-700 focus:bg-white focus:border-teal-500/50 focus:outline-none transition-all"
                >
                  <option value="All">All</option>
                  <option value="Upcoming">Upcoming</option>
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                  <ChevronLeft size={16} className="-rotate-90" />
                </div>
              </div>
            </div>

            {/* Doctor Name Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                <Stethoscope size={12} className="text-teal-600" />
                Doctor Name
              </label>
              <input
                type="text"
                value={doctorNameFilter}
                onChange={(e) => {
                  setDoctorNameFilter(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search doctor name"
                className="w-full h-11 rounded-xl border border-slate-200 bg-slate-50/50 px-4 text-sm text-slate-700 focus:bg-white focus:border-teal-500/50 focus:outline-none transition-all"
              />
            </div>

            {/* Appointment ID Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                <Hash size={12} className="text-teal-600" />
                Appointment ID
              </label>
              <input
                type="text"
                value={appointmentIdFilter}
                onChange={(e) => {
                  setAppointmentIdFilter(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Enter appointment ID"
                className="w-full h-11 rounded-xl border border-slate-200 bg-slate-50/50 px-4 text-sm text-slate-700 focus:bg-white focus:border-teal-500/50 focus:outline-none transition-all"
              />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden mb-8">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
            <p className="text-sm font-semibold text-slate-700">
              Total Appointments: <span className="text-teal-700">{totalElements}</span>
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 image.png">
              <thead className="bg-sky-50">
                <tr>
                  <th className="px-6   text-left text-sm font-semibold text-[#4B5563]   tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <Hash size={12} className="text-slate-400" />
                      ID
                    </div>
                  </th>
                  <th className="px-6 py-1  text-left text-sm font-semibold text-[#4B5563]   tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-teal-600" />
                      Patient Details
                    </div>
                  </th>
                  <th className="px-6 py-1  text-left text-sm font-semibold text-[#4B5563]   tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <Stethoscope size={14} className="text-teal-600" />
                      Doctor Details
                    </div>
                  </th>
                  <th className="px-6 py-1 text-left text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <Clock size={18} className="text-slate-400" />
                      Consultation Time
                    </div>
                  </th>
                  
                  <th className="px-6 py-1  text-left text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <Calendar size={18} className="text-slate-400" />
                      Appointment Date
                    </div>
                  </th>
                  <th className="px-6 py-1 text-left text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <CalendarCheck size={18} className="text-slate-400" />
                      Booking Details
                    </div>
                  </th>
                  <th className="px-6 py-1 text-center text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center justify-center gap-2">
                      <Zap size={14} className="text-amber-500" />
                      Status
                    </div>
                  </th>
                  <th className="px-6 py-1  text-left text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <MessageSquare size={14} className="text-slate-400" />
                      Remarks
                    </div>
                  </th>
                  <th className="px-6 py-1  text-right text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center justify-end gap-2">
                      <Settings size={12} className="text-slate-400" />
                      Action
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-20 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="relative h-12 w-12">
                          <div className="absolute inset-0 rounded-full border-4 border-teal-50 border-t-teal-600 animate-spin" />
                        </div>
                        <span className="text-sm font-black text-slate-400 uppercase tracking-widest animate-pulse">Synchronizing Data...</span>
                      </div>
                    </td>
                  </tr>
                ) : consultations.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="h-16 w-16 rounded-3xl bg-slate-50 flex items-center justify-center">
                          <Eye size={32} className="text-slate-200" />
                        </div>
                        <span className="text-sm font-semibold text-slate-400">No matching consultation records found</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  consultations.map((item) => {
                    const patientName = `${item?.salutation || ""} ${item?.firstName || ""} ${item?.lastName || ""}`.trim();
                    const doctorName = `${item?.doctorSalutation || ""} ${item?.doctorFirstName || ""} ${item?.doctorLastName || ""}`.trim();
                    const apptDate = item?.whenAppointment ? format(new Date(item.whenAppointment), "dd-MM-yyyy") : "-";
                    const bookingDate = item?.createdDate ? format(new Date(item.createdDate), "dd-MM-yyyy") : "-";

                    const isDoctorNotJoined = (() => {
                      if (!item?.fromTime || item?.doctorStartTime) return false;
                      try {
                        if (!item?.whenAppointment) return false;
                        const [hour, minute] = String(item.fromTime).split(":").map(Number);
                        const scheduled = new Date(item.whenAppointment);
                        scheduled.setHours(hour, minute, 0, 0);
                        return new Date() > scheduled;
                      } catch { return false; }
                    })();

                    return (
                      <tr key={item?.id} className={`group transition-all hover:bg-teal-50/20 ${isDoctorNotJoined ? "bg-rose-50/40" : ""}`}>
                        <td className="px-5 py-5 whitespace-nowrap">
                          <div className="flex items-center gap-2 px-3 py-1 rounded-lg w-fit">

                            <span className="text-sm text-slate-800 tracking-tight">{item?.id ?? "-"}</span>
                          </div>
                        </td>
                        <td className="px-5 py-5 min-w-[280px]">
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2.5">
                              <div className="h-5 w-5 rounded-full bg-teal-50 border border-teal-100 flex items-center justify-center flex-shrink-0 shadow-sm">
                                <User size={13} className="text-teal-600" />
                              </div>
                              <span className="text-sm text-slate-900 leading-tight">{patientName || "-"}</span>
                            </div>
                            <div className="flex items-center gap-2.5 ml-10.5">
                              <span className={`text-sm px-2 py-0.5 rounded-lg  tracking-wide border ${item?.consultationsType === 'V' ? 'bg-indigo-50/70 text-sky-700 border-indigo-200/50' : 'bg-emerald-50/70 text-emerald-700 border-emerald-200/50'}`}>
                                {item?.consultationsType === 'V' ? 'Video' : 'In-Person'}
                              </span>
                              <div className="flex items-center gap-1.5 ml-2">
                                <Phone size={12} className="text-teal-600/70" />
                                <span className="text-sm text-slate-500">{item?.mobile || "-"}</span>
                              </div>
                            </div>
                            {item?.prescriptionType?.toLowerCase?.() === "curebayscribe" && (
                              <div className="ml-10.5 w-fit flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1">
                                <MessageSquare size={12} className="text-violet-600" />
                                <span className="text-xs font-medium tracking-wide text-violet-700">
                                  AI Scribe Processed
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-5 min-w-[260px]">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2.5">
                              <div className="h-5 w-5 rounded-lg bg-teal-600 flex items-center justify-center shadow flex-shrink-0">
                                <Stethoscope size={12} className="text-white" />
                              </div>
                              <span className="text-sm text-slate-800 leading-tight">{doctorName || "-"}</span>
                            </div>
                            <span className="text-sm text-teal-600  tracking-widest ml-10.5 pl-2 ml-6 mt-0.5">{item?.doctorSpeciality || "Specialist"}</span>
                          </div>
                        </td>
                        <td className="px-5 py-5 whitespace-nowrap">
                          <div className="flex items-center gap-2 bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 w-fit">
                            <Clock size={12} className="text-slate-400" />
                            <span className="text-sm  text-slate-600 tracking-tighter">
                              {item?.doctorStartTime ? format(new Date(item.doctorStartTime), "HH:mm") : "NA"}
                              {" - "}
                              {item?.doctorEndTime ? format(new Date(item.doctorEndTime), "HH:mm") : "NA"}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-5 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <Calendar size={13} className="text-teal-600" />
                              <span className="text-sm text-slate-800 tracking-tighter">{apptDate}</span>
                            </div>
                            <div className="flex items-center gap-2 ml-6">
                              <div className="h-1 w-1 rounded-full bg-teal-400" />
                              <span className="text-sm  text-slate-500 uppercase tracking-widest">{item?.fromTime || "-"}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-5 whitespace-nowrap">
                          <div className="flex items-start gap-2 text-slate-500">
                           
                            <div className="flex flex-col">
                              <span className="text-sm text-slate-800 uppercase tracking-wider">{bookingDate}</span>
                              <span className="text-sm text-slate-800 uppercase tracking-wider mt-2">
                                {item?.cheBranchId
                                  ? item.cheBranchId.replace(/^LOC_/i, "")
                                  : item?.sourceChannel}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-5 whitespace-nowrap text-center">
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl border-2 text-sm tracking-widest shadow-sm ${getStatusStyle(item?.status)}`}>
                            <Zap size={10} className="opacity-70 fill-current" />
                            {getStatusLabel(item?.status)}
                          </div>
                        </td>
                        <td className="px-5 py-5 min-w-[220px]">
                          <div className="flex items-start gap-2 bg-slate-50/50 p-2 rounded-xl border border-slate-100/50">
                            <MessageSquare size={14} className="text-teal-500 mt-1 flex-shrink-0" />
                            <span className="text-sm  text-slate-600 line-clamp-2 leading-relaxed" title={item?.remarks}>
                              {item?.remarks || "No clinical remarks available"}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-5 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={() => handleOpenRemarkModal(item)} className="h-9 w-9 flex items-center justify-center rounded-xl text-teal-600 hover:bg-teal-600 hover:text-white transition-all bg-white shadow-sm border border-slate-100" title="Add Remark">
                              <MessageSquare size={18} title="Add Remark" />
                            </button>
                            {[1, 8].includes(Number(item?.status)) && (
                              <button onClick={() => handleOpenShareModal(item)} className="h-9 w-9 flex items-center justify-center rounded-xl text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all bg-white shadow-sm border border-slate-100" title="Share Link">
                                <Share2 size={18} />
                              </button>
                            )}
                            {Number(item?.status) === 2 && (
                              <>
                                <button onClick={() => handleOpenPrescriptionModal(item)} className="h-9 w-9 flex items-center justify-center rounded-xl text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all bg-white shadow-sm border border-slate-100" title="View Prescription">
                                  <Eye size={18} />
                                </button>
                                <button onClick={() => handleOpenEditPrescriptionPage(item)} className="h-9 w-9 flex items-center justify-center rounded-xl text-amber-600 hover:bg-amber-600 hover:text-white transition-all bg-white shadow-sm border border-slate-100" title="Edit Prescription">
                                  <Edit size={18} />
                                </button>
                              </>
                            )}
                            {isDoctorNotJoined && <span className="bg-rose-500 text-white text-[10px] font-black px-2 py-1 rounded-xl ml-2 shadow-sm animate-pulse tracking-widest">LATE</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {showTableScrollbar && (
            <div className="px-4 pb-3">
              <div
                ref={tableScrollbarRef}
                className="consultation-list-alwaysbar overflow-x-scroll"
                style={{
                  height: 12,
                }}
              >
                <div ref={tableScrollbarSpacerRef} style={{ height: 1 }} />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end border-t border-slate-200 p-4">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalElements}
              showSizeChanger
              onChange={(page, size) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                  setCurrentPage(1);
                }
              }}
            />
          </div>
        </div>
      </div>
      {isPrescriptionModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-3 sm:p-5"
          onClick={handleClosePrescriptionModal}
        >
          <div
            className="relative flex h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3 sm:px-6">
              <div>
                <h2 className="text-base font-semibold text-slate-900 sm:text-lg">
                  Prescription Preview
                </h2>
                <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
                  {selectedPatientName}
                </p>
              </div>
              <button
                type="button"
                onClick={handleClosePrescriptionModal}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
              >
                Close
              </button>
            </div>
            <div className="h-[calc(94vh-72px)] w-full bg-slate-100 p-2 sm:p-3">
              {isPrescriptionLoading ? (
                <div className="flex h-full w-full flex-col items-center justify-center gap-4 rounded-lg border border-slate-200 bg-white">
                  <div className="relative h-12 w-12">
                    <div className="absolute inset-0 rounded-full border-4 border-teal-50 border-t-teal-600 animate-spin" />
                  </div>
                  <span className="text-sm font-black text-slate-400 uppercase tracking-widest animate-pulse">
                    Loading Prescription...
                  </span>
                </div>
              ) : prescriptionError ? (
                <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white">
                  <span className="text-sm font-medium text-rose-600">{prescriptionError}</span>
                </div>
              ) : (
                <iframe
                  src={`${selectedPrescriptionUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  title="Prescription PDF"
                  className="h-full w-full rounded-lg border border-slate-200 bg-white"
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
      {isShareModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white px-6 py-5 shadow-2xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Share</h2>
              <button
                type="button"
                onClick={handleCloseShareModal}
                className="rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close"
              >
                <X size={28} />
              </button>
            </div>
            <div className="mb-6">
              <input
                type="text"
                value={shareRecipient}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setShareRecipient(value);
                  if (shareError) setShareError("");
                }}
                placeholder="Enter Email/Mobile"
                minLength={10}
                maxLength={10}
                inputMode="numeric"
                className="w-full border-b border-slate-300 px-1 py-2 text-xl text-slate-700 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none"
              />
              {shareError ? (
                <p className="mt-2 text-sm text-red-600">{shareError}</p>
              ) : null}
            </div>
            <div className="flex items-center justify-end gap-4">
              <button
                type="button"
                onClick={handleCloseShareModal}
                className="min-w-[120px] rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-bold text-white transition-all hover:bg-teal-700 shadow-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendShareCall}
                disabled={shareSending}
                className="min-w-[120px] rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-bold text-white transition-all hover:bg-teal-700 shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                {shareSending ? "Sending..." : "Send Link"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {isRemarkModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white px-6 py-5 shadow-2xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Add Remark</h2>
              <button
                type="button"
                onClick={handleCloseRemarkModal}
                className="rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close"
              >
                <X size={28} />
              </button>
            </div>
            <div className="mb-6">
              <textarea
                value={remarkText}
                onChange={(e) => {
                  setRemarkText(e.target.value);
                  if (remarkError) setRemarkError("");
                }}
                rows={4}
                placeholder="Enter remark"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none"
              />
              {remarkError ? (
                <p className="mt-2 text-sm text-red-600">{remarkError}</p>
              ) : null}
            </div>
            <div className="flex items-center justify-end gap-4">
              <button
                type="button"
                onClick={handleCloseRemarkModal}
                className="min-w-[120px] rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-bold text-white transition-all hover:bg-teal-700 shadow-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitRemark}
                disabled={remarkSaving}
                className="min-w-[120px] rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-bold text-white transition-all hover:bg-teal-700 shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                {remarkSaving ? "Submitting..." : "Submit Remark"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
};

export default ConsultationList;
